import React, { useState } from 'react';
import {
  Flex,
  Accordion,
  Text,
  Button,
  IconButton
} from 'onefaceui-react-components';
import PropTypes from 'prop-types';

import { getResourceTo } from 'react/utils/utils';
import { actionCreators as bulkDownloadActions } from 'react/api/actions/bulkDownload';
import AlertGenericModal from 'react/containers/modals/AlertGenericModal';
import Loading from 'react/components/Loading';
import { useDispatch } from 'react-redux';

const headerComponent = ({ description, onRemoveItem }) => (
  <Flex
    gap={onRemoveItem ? 0 : 10}
    alignItems="center"
    justifyContent={onRemoveItem ? 'space-between' : 'flex-start'}
    className="activityCenterItem-header"
  >
    <Flex gap={10} alignItems="center">
      <span className="icon-download" />
      <Flex flexDirection="column" className="photoItemHeader">
        <Text size="sm">{getResourceTo('activityCenter.photo.title')}</Text>
        <Text size="xs" type="neutral" className="photoItemHeaderDescription">
          {description}
        </Text>
      </Flex>
    </Flex>
    {onRemoveItem && (
      <IconButton
        variant="neutral"
        onClick={(e) => {
          e.stopPropagation();
          onRemoveItem();
        }}
        dataTestId="remove-item-btn"
      >
        <span className="icon-close" />
      </IconButton>
    )}
  </Flex>
);

function ActivityCenterPhotoItem({
  isPending, date, totalFiles, urlFile, id
}) {
  const dispatch = useDispatch();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const totalDescription = getResourceTo(
    'activityCenter.photo.loading.description',
    totalFiles
  );
  const doneDescription = `${getResourceTo(
    'activityCenter.photo.description',
    totalFiles
  )} - ${date}`;

  const handleRemoveItem = () => {
    dispatch(bulkDownloadActions.removeDownload(id));
  };

  const handleConfirmCancel = () => {
    dispatch(bulkDownloadActions.cancelDownload(id));
  };

  if (isPending) {
    return (
      <>
        <Accordion
          customHeaderComponent={headerComponent({
            description: totalDescription
          })}
          dataTestId={`activityCenter-item-photo-${id}`}
          isExpanded
        >
          <Flex
            flexDirection="column"
            className="activityCenterItem-content"
            gap={8}
            dataTestId={`activityCenter-item-photo-${id}-content`}
          >
            <Text
              size="sm"
              className="activityCenterItem-content-title"
              dataTestId="photo-text-pending-title"
            >
              <Loading width="24px" height="24px" />
              {getResourceTo('activityCenter.photo.loading.title')}
            </Text>
            <Text
              size="xs"
              type="neutral"
              dataTestId="photo-text-pending-description"
            >
              {getResourceTo('activityCenter.photo.loading.pendingText')}
            </Text>
          </Flex>
          <Flex
            alignItems="center"
            justifyContent="flex-end"
            className="activityCenterItem-footer"
          >
            <Button
              variant="secondary"
              onClick={() => setConfirmCancel(true)}
              dataTestId="photo-btn-cancel"
            >
              {getResourceTo('activityCenter.photo.loading.cancelBtn')}
            </Button>
          </Flex>
        </Accordion>
        <AlertGenericModal
          open={confirmCancel}
          i18n={Monet.I18n}
          title="ModalLogout.Title"
          message="ModalLogout.Content"
          confirmClickButton={handleConfirmCancel}
          cancelClickButton={() => setConfirmCancel(() => false)}
          customConfirmButtonResource="ModalLogout.ConfirmButton"
        />
      </>
    );
  }

  return (
    <Flex
      dataTestId={`activityCenter-item-photo-${id}`}
      className="expanded-item"
      flexDirection="column"
    >
      {headerComponent({
        description: doneDescription,
        onRemoveItem: handleRemoveItem
      })}
      <Flex
        flexDirection="column"
        className="activityCenterItem-content"
        gap={8}
      >
        <Text
          size="xs"
          type="neutral"
          dataTestId="photo-text-download"
          className="photoTextDownload"
        >
          {getResourceTo('activityCenter.photo.loading.doneText1')}
          {' '}
          {getResourceTo('activityCenter.photo.loading.doneText2')}
          <a
            href={urlFile}
            target="_blank"
            className="photoLinkDownload"
            rel="noreferrer"
          >
            {getResourceTo('activityCenter.photo.loading.doneText3')}
          </a>
          {getResourceTo('activityCenter.photo.loading.doneText4')}
        </Text>
      </Flex>
    </Flex>
  );
}

ActivityCenterPhotoItem.propTypes = {
  isPending: PropTypes.bool.isRequired,
  date: PropTypes.string.isRequired,
  totalFiles: PropTypes.number.isRequired,
  urlFile: PropTypes.string,
  id: PropTypes.string.isRequired
};

ActivityCenterPhotoItem.defaultProps = {
  urlFile: null
};

export { ActivityCenterPhotoItem };
